import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetClientsQuery } from "../../../redux/services/clients/clientsApi";
import { useGetJobsQuery } from "../../../redux/services/jobs/jobsApi";
import { useCreateInvoiceV2Mutation } from "../../../redux/services/invoices/invoicesV2Api";
import InvoiceV2Editor from "../components/InvoiceV2Editor";
import { InvoiceTagsInline } from "../../../components";
import {
  buildTermsState,
  buildInvoiceV2Payload,
  buildTotals,
  createLineItem,
  createLineItemFromJob,
  createTotalsAdjustments,
  DEFAULT_TAX_SETTINGS,
  extractJobs,
} from "../utils";

const InvoiceV2Create = () => {
  const navigate = useNavigate();
  const initialTerms = buildTermsState({
    terms: "Net 15",
    termsCode: "net_15",
    netDays: 15,
  });
  const [client, setClient] = useState(null);
  const [status, setStatus] = useState("review");
  const [terms, setTerms] = useState(initialTerms.terms);
  const [termsCode, setTermsCode] = useState(initialTerms.termsCode);
  const [netDays, setNetDays] = useState(initialTerms.netDays);
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [dueDate, setDueDate] = useState(initialTerms.dueDate);
  const [taxSettings, setTaxSettings] = useState(DEFAULT_TAX_SETTINGS);
  const [lineItems, setLineItems] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [fees, setFees] = useState([]);
  const [credits, setCredits] = useState([]);
  const [adjustments] = useState(createTotalsAdjustments());
  const [tagIds, setTagIds] = useState([]);

  const { data: clientsData, isLoading: clientsLoading } = useGetClientsQuery({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
  });
  const { data: jobsData, isLoading: jobsLoading } = useGetJobsQuery(
    {
      page: 1,
      limit: 100,
      client: client?._id,
      invoiceId: false,
      sortBy: "transactionDate",
      sortOrder: "desc",
    },
    { skip: !client?._id }
  );
  const [createInvoiceV2, { isLoading: creating }] = useCreateInvoiceV2Mutation();

  const jobs = useMemo(() => extractJobs(jobsData), [jobsData]);
  const totals = useMemo(
    () => buildTotals(lineItems, adjustments, discounts, fees, credits),
    [adjustments, credits, discounts, fees, lineItems]
  );

  function syncTerms(updates = {}) {
    const nextTerms = buildTermsState({
      terms,
      termsCode,
      netDays,
      paymentInstructions,
      dueDate: updates.dueDate !== undefined ? updates.dueDate : dueDate,
      ...updates,
    });

    setTerms(nextTerms.terms);
    setTermsCode(nextTerms.termsCode);
    setNetDays(nextTerms.netDays);
    setPaymentInstructions(nextTerms.paymentInstructions);
    setDueDate(nextTerms.dueDate);
  }

  function updateLineItem(localId, updates) {
    setLineItems((current) =>
      current.map((lineItem) =>
        lineItem.localId === localId ? { ...lineItem, ...updates } : lineItem
      )
    );
  }

  function removeLineItem(localId) {
    setLineItems((current) => current.filter((lineItem) => lineItem.localId !== localId));
  }

  async function handleSubmit() {
    const payload = buildInvoiceV2Payload({
      client,
      status,
      terms,
      termsCode,
      netDays,
      paymentInstructions,
      dueDate,
      taxSettings,
      lineItems,
      discounts,
      fees,
      credits,
      totals,
      tags: tagIds,
    });

    try {
      const response = await createInvoiceV2(payload).unwrap();
      toast.success(response.message || "Created Invoice V2", {
        toastId: "invoice-v2-created",
      });
      navigate(`/invoices/v2/${response.data._id}`);
    } catch (error) {
      toast.error(error?.data?.message || "Unable to create invoice", {
        toastId: "invoice-v2-create-error",
      });
    }
  }

  return (
    <InvoiceV2Editor
      title="Invoice V2"
      subtitle="Route-only workflow for the new line-item invoice model."
      titleInlineExtras={
        <InvoiceTagsInline
          invoice={{ tags: tagIds }}
          editable
          onChangeTags={(next) => setTagIds(next)}
          badgeSize="lg"
        />
      }
      clientsData={clientsData}
      clientsLoading={clientsLoading}
      jobs={jobs}
      jobsLoading={jobsLoading}
      client={client}
      onClientChange={setClient}
      status={status}
      onStatusChange={setStatus}
      terms={terms}
      onTermsChange={(value) => syncTerms({ terms: value })}
      termsCode={termsCode}
      onTermsCodeChange={(value) => syncTerms({ termsCode: value, dueDate: "" })}
      netDays={netDays}
      onNetDaysChange={(value) => syncTerms({ netDays: value, dueDate: "" })}
      paymentInstructions={paymentInstructions}
      onPaymentInstructionsChange={(value) => syncTerms({ paymentInstructions: value })}
      dueDate={dueDate}
      onDueDateChange={setDueDate}
      taxSettings={taxSettings}
      onTaxSettingsChange={setTaxSettings}
      lineItems={lineItems}
      onLineItemChange={updateLineItem}
      onLineItemRemove={removeLineItem}
      onAddManualLine={() =>
        setLineItems((current) => [...current, createLineItem("manual", taxSettings)])
      }
      onAddJobLines={(selectedJobs) =>
        setLineItems((current) => [
          ...current,
          ...selectedJobs.map((job) => createLineItemFromJob(job, taxSettings)),
        ])
      }
      discounts={discounts}
      onDiscountsChange={setDiscounts}
      fees={fees}
      onFeesChange={setFees}
      credits={credits}
      onCreditsChange={setCredits}
      totals={totals}
      submitLabel="Create Invoice V2"
      onSubmit={handleSubmit}
      submitting={creating}
      onBack={() => navigate("/invoices/v2")}
    />
  );
};

export default InvoiceV2Create;
